import { BotWalletSupportedChain } from "@/providers/wallet";
import {
  formatNativeAmountForDisplay,
  formatTokenAmountForDisplay,
} from "@/utils/currency";
import { api, type Api } from "@api/bot-base-api-request";
import { useQuery } from "@tanstack/react-query";
import { SelectMultipleButtonOption } from "@ui/select-multiple-buttons.tsx";
import { useCallback } from "react";
import { BotOrder, Order } from "./types";

const BOT_GET_ORDERS_QUERY_KEY = "bot-get-orders";

type BotGetOrdersParams = {
  userId: string;
  appPubKey: string;
  chainId: number;
  walletAddress: string;
  tokenAddress?: string;
  filters?: {
    price?: {
      min?: number;
      max?: number;
    };
    outflow?: {
      min?: number;
      max?: number;
    };
    inflow?: {
      min?: number;
      max?: number;
    };
    value?: {
      min?: number;
      max?: number;
    };
    txType?: SelectMultipleButtonOption[];
    time?: {
      min?: number;
      max?: number;
    };
  };
};

const transformParams = (params: BotGetOrdersParams) => {
  const result: Record<string, string | number> = {};
  if (params.userId != null) {
    result.userId = params.userId;
  }
  if (params.appPubKey != null) {
    result.appPubKey = params.appPubKey;
  }
  if (params.chainId != null) {
    result.chainId = params.chainId;
  }
  if (params.walletAddress != null) {
    result.walletAddress = params.walletAddress;
  }
  if (params.filters != null && Object.keys(params.filters).length > 0) {
    result.filters = JSON.stringify(params.filters);
  }

  return result;
};

// Response type from the API
interface BotOrdersResponse {
  buyOrders: BotOrder[];
  sellOrders: BotOrder[];
}

const defaultSelector = (
  data: BotOrdersResponse,
  params: BotGetOrdersParams,
): Order[] => {
  const result: Order[] = [];
  const isNotFilteredByType = !params.filters?.txType?.length;

  if (isNotFilteredByType || params.filters?.txType?.includes("Buy")) {
    data.buyOrders.forEach((buyOrder) => {
      if (
        params.tokenAddress &&
        buyOrder.tokenInfo.address !== params.tokenAddress
      ) {
        return;
      }
      const order = transformOrderItem(buyOrder, params, "buy");
      result.push(order);
    });
  }
  if (isNotFilteredByType || params.filters?.txType?.includes("Sell")) {
    data.sellOrders.forEach((sellOrder) => {
      if (
        params.tokenAddress &&
        sellOrder.tokenInfo.address !== params.tokenAddress
      ) {
        return;
      }
      const order = transformOrderItem(sellOrder, params, "sell");
      result.push(order);
    });
  }

  return result;
};

const transformOrderItem = (
  item: BotOrder,
  params: BotGetOrdersParams,
  type: "buy" | "sell",
): Order => {
  let inflow = "0";
  let value = 0;
  if (item.toTokenAmount !== "Infinity" && item.toTokenAmount !== "NaN") {
    inflow =
      type === "buy"
        ? formatTokenAmountForDisplay(
            item.toTokenAmount,
            item.tokenInfo.decimals,
          )
        : formatNativeAmountForDisplay(item.toTokenAmount, params.chainId);
    if (type === "buy") {
      value =
        Number(item.price) *
        Number(
          formatTokenAmountForDisplay(
            item.toTokenAmount,
            item.tokenInfo.decimals,
          ),
        );
    }
  }
  if (type === "sell") {
    value =
      Number(item.price) *
      Number(
        formatTokenAmountForDisplay(
          item.fromTokenAmount,
          item.tokenInfo.decimals,
        ),
      );
  }
  const outflow =
    type === "buy"
      ? formatNativeAmountForDisplay(item.fromTokenAmount, params.chainId)
      : formatTokenAmountForDisplay(
          item.fromTokenAmount,
          item.tokenInfo.decimals,
        );

  const order: Order = {
    orderId: item.orderId,
    type: type,
    wallet: item.userWallet,
    chain:
      params.chainId === BotWalletSupportedChain.SOL ? "solana" : "ethereum",
    price: item.price,
    outflow: outflow,
    inflow: inflow,
    value: value.toString(),
    createdAt: item.createdAtMs,
    tokenInfo: item.tokenInfo,
    expectedPnl: item.expectedPnl,
    expectedRoi: item.expectedRoi,
    takeProfitPrice: item.takeProfitPrice,
  };

  return order;
};

const fetchBotOrders = async (
  api: Api,
  params: BotGetOrdersParams,
): Promise<BotOrdersResponse> =>
  api.get("orders", { searchParams: transformParams(params) }).json();

const useGetBotOrders = (
  params: BotGetOrdersParams,
  selector: (data: BotOrdersResponse) => Order[] = (data) =>
    defaultSelector(data, params),
) =>
  useQuery<BotOrdersResponse, Error, Order[]>({
    queryKey: [BOT_GET_ORDERS_QUERY_KEY, params],
    queryFn: async () => fetchBotOrders(api, params),
    select: useCallback(selector, [selector, params]),
  });

export {
  BOT_GET_ORDERS_QUERY_KEY,
  fetchBotOrders,
  useGetBotOrders,
  type BotOrdersResponse,
  type Order,
};
